import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { Check, Search, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { catalogPuppetsInfiniteQuery, defaultSpeaks, puppetsQuery, type CatalogPuppet, type PuppetFeedItem, type PuppetKind } from '@/api/puppets'
import { Button } from '@/components/ui/button'
import { Chip } from '@/components/ui/chip'
import { Input } from '@/components/ui/input'
import { cn } from '@/components/ui/utils'
import { PuppetThumb } from '@/features/puppets/PuppetCard'

/**
 * "Who's in the story?" — puppets that are ready first (`GET /api/skel/puppets`), then every
 * catalog character, animal and prop (`GET /api/skel/assets`). A catalog pick that cannot move yet
 * is fine: the server teaches it while the story is written (§3.4).
 *
 * The tiles never move under the pointer. While a rig runs in the background ("Make all move"
 * refreshes the lists every few seconds) puppets keep turning ready; re-sorting the ready row, or
 * moving an asset from "More friends" up into it, shifted every tile mid-click and the click landed
 * on the wrong friend or on nothing. So who is in "Ready to act" — and therefore which catalog
 * tiles it hides — is decided once, when the feed first answers; later answers only update the
 * tiles in place (a "Needs moves" tile turning ready, a "Learns to move" note going away). A puppet
 * that turns ready meanwhile stays where it is, in the catalog, and is picked from there.
 */

export interface CastPick {
  uid: string
  name: string
  thumb: string | null
  kind: PuppetKind | null
  /** Talks unless it is a prop (the wizard may still quiet it for the age band). */
  speaks: boolean
  /** A catalog asset that is not a puppet yet: it learns to move while the story is made. */
  learning: boolean
  /** Arrived through `?cast=` and its name / picture are still being looked up. */
  pending?: boolean
  /** Why the AI suggested this library character; absent for manual additions. */
  reason?: string
}

export const MAX_CAST = 3
/** Ready tiles shown before "Show more" (every catalog asset may be a puppet: hundreds of them). */
export const READY_STEP = 48

const FILTERS: Array<{ id: string; label: string; kinds: (PuppetKind | undefined)[] }> = [
  { id: 'all', label: 'Everyone', kinds: [undefined] },
  { id: 'people', label: 'People', kinds: ['person'] },
  { id: 'animals', label: 'Animals', kinds: ['animal', 'creature'] },
  { id: 'birds', label: 'Birds', kinds: ['bird'] },
  { id: 'sea', label: 'Sea', kinds: ['fish'] },
  { id: 'props', label: 'Props', kinds: ['object'] },
]

export function pickFromFeed(p: PuppetFeedItem): CastPick {
  return { uid: p.uid, name: p.name, thumb: p.thumb_url, kind: p.kind, speaks: p.speaks ?? defaultSpeaks(p.kind), learning: false }
}

export function pickFromCatalog(c: CatalogPuppet): CastPick {
  return { uid: c.asset_uid, name: c.name, thumb: c.thumb_url, kind: c.kind, speaks: defaultSpeaks(c.kind), learning: c.link?.status !== 'ready' }
}

export interface CastPickerProps {
  picks: CastPick[]
  onToggle(pick: CastPick): void
  max?: number
}

export function CastPicker({ picks, onToggle, max = MAX_CAST }: CastPickerProps) {
  const [q, setQ] = useState('')
  const [filter, setFilter] = useState('all')
  const kinds = FILTERS.find((f) => f.id === filter)?.kinds ?? [undefined]
  const needle = q.trim().toLowerCase()

  const [qSent, setQSent] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setQSent(q.trim()), 250)
    return () => clearTimeout(t)
  }, [q])

  const feed = useQuery(puppetsQuery({}))
  const catalog = useInfiniteQuery(catalogPuppetsInfiniteQuery(kinds, qSent, 48))

  // The "Ready to act" roster, fixed at the feed's first answer (see the note at the top).
  const rosterRef = useRef<Roster | null>(null)
  if (rosterRef.current === null && (feed.isSuccess || feed.isError)) rosterRef.current = makeRoster(feed.data?.items ?? [])
  const roster = rosterRef.current

  const feedItems = useMemo(() => {
    if (!roster) return []
    const rows: { item: PuppetFeedItem; rank: number }[] = []
    for (const p of feed.data?.items ?? []) {
      let rank = roster.order.get(p.uid)
      if (rank === undefined) {
        // New since the roster was made. A catalog asset's puppet is already on screen as its
        // catalog tile; only a puppet with no catalog tile (made from a parts sheet) joins, last.
        if (p.asset_uid) continue
        rank = roster.order.size
        roster.order.set(p.uid, rank)
      }
      if ((filter === 'all' || (p.kind != null && kinds.includes(p.kind))) && (!needle || p.name.toLowerCase().includes(needle))) rows.push({ item: p, rank })
    }
    return rows.sort((a, b) => a.rank - b.rank).map((r) => r.item)
  }, [roster, feed.data, filter, kinds, needle])

  const [readyLimit, setReadyLimit] = useState(READY_STEP)
  useEffect(() => setReadyLimit(READY_STEP), [filter, needle])

  const catalogItems = useMemo(() => {
    if (!roster) return []
    const seen = new Set<string>()
    const out: CatalogPuppet[] = []
    for (const page of catalog.data?.pages ?? []) {
      for (const c of page.items) {
        // A catalog asset already shown as a ready puppet is not offered twice.
        if (seen.has(c.asset_uid) || roster.assets.has(c.asset_uid) || (c.link?.project_id && roster.projects.has(c.link.project_id))) continue
        seen.add(c.asset_uid)
        out.push(c)
      }
    }
    return out
  }, [catalog.data, roster])

  const pickedUids = new Set(picks.map((p) => p.uid))
  const full = picks.length >= max

  return (
    <div className="flex flex-col gap-5">
      <CastTray picks={picks} onToggle={onToggle} max={max} />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-56 flex-1 sm:max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-3" aria-hidden />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a friend: bear, owl, princess…" aria-label="Search for a friend" className="pl-9" />
        </div>
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Show">
          {FILTERS.map((f) => (
            <Chip key={f.id} selected={filter === f.id} onClick={() => setFilter(f.id)}>
              {f.label}
            </Chip>
          ))}
        </div>
      </div>

      {feedItems.length > 0 ? (
        <section aria-labelledby="cast-ready" className="flex flex-col gap-2">
          <h3 id="cast-ready" className="text-sm font-semibold text-ink">
            Ready to act
          </h3>
          <ul className={TILE_GRID} role="list">
            {feedItems.slice(0, readyLimit).map((p) => (
              <PickTile
                key={p.uid}
                pick={pickFromFeed(p)}
                picked={pickedUids.has(p.uid)}
                disabled={!p.story_ready || (full && !pickedUids.has(p.uid))}
                note={p.story_ready ? null : 'Needs moves'}
                onToggle={onToggle}
              />
            ))}
          </ul>
          {feedItems.length > readyLimit ? (
            <Button variant="secondary" className="self-center" onClick={() => setReadyLimit((n) => n + READY_STEP)}>
              Show more
            </Button>
          ) : null}
        </section>
      ) : null}

      <section aria-labelledby="cast-more" className="flex flex-col gap-2">
        <h3 id="cast-more" className="text-sm font-semibold text-ink">
          {feedItems.length > 0 ? 'More friends' : 'Friends'} <span className="font-normal text-ink-3">— the ones that can’t move yet learn while we write</span>
        </h3>
        {catalog.isPending || !roster ? (
          <p className="text-sm text-ink-3">Looking for friends…</p>
        ) : catalog.isError ? (
          <p className="text-sm text-danger" role="alert">
            The friends could not be loaded.{' '}
            <button type="button" className="underline" onClick={() => void catalog.refetch()}>
              Try again
            </button>
          </p>
        ) : catalogItems.length === 0 ? (
          <p className="text-sm text-ink-3">{needle ? `Nobody called “${q}”.` : 'Nobody here yet.'}</p>
        ) : (
          <ul className={TILE_GRID} role="list" data-testid="cast-catalog">
            {catalogItems.map((c) => {
              const pick = pickFromCatalog(c)
              return (
                <PickTile
                  key={c.asset_uid}
                  pick={pick}
                  picked={pickedUids.has(c.asset_uid)}
                  disabled={full && !pickedUids.has(c.asset_uid)}
                  note={pick.learning ? 'Learns to move' : null}
                  onToggle={onToggle}
                />
              )
            })}
          </ul>
        )}
        {catalog.hasNextPage ? (
          <Button variant="secondary" className="self-center" onClick={() => void catalog.fetchNextPage()} loading={catalog.isFetchingNextPage}>
            Show more
          </Button>
        ) : null}
      </section>
    </div>
  )
}

export const TILE_GRID = 'grid grid-cols-[repeat(auto-fill,minmax(8.5rem,1fr))] gap-3'

export function CastReasons({ picks, onHero }: { picks: CastPick[]; onHero(uid: string): void }) {
  return (
    <ul className="flex flex-col gap-3" aria-label="Cast roles and reasons">
      {picks.map((pick, i) => (
        <li key={pick.uid} className="flex flex-wrap items-center gap-3 rounded-xl bg-paper-2 p-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-ink">{pick.name} · {i === 0 ? 'Hero' : 'Friend'}{pick.speaks ? '' : ' · Listens'}</p>
            {pick.reason ? <p className="mt-1 text-sm text-ink-2">{pick.reason}</p> : null}
          </div>
          {i > 0 ? <Button variant="secondary" onClick={() => onHero(pick.uid)} aria-label={`Make ${pick.name} the hero`}>Make hero</Button> : null}
        </li>
      ))}
    </ul>
  )
}

export function CastTray({ picks, onToggle, max = MAX_CAST }: CastPickerProps) {
  return (
    <div className="flex min-h-14 flex-wrap items-center gap-2 rounded-2xl border border-dashed border-line-strong p-2" aria-label="Picked" data-testid="cast-tray">
      {picks.length === 0 ? <p className="px-2 text-sm text-ink-2">Nobody yet — tap a friend below.</p> : picks.map((p) => (
        <span key={p.uid} className="inline-flex h-11 items-center gap-2 rounded-full bg-accent-soft pl-1 pr-1.5 text-sm font-medium text-ink">
          <span className="size-9 overflow-hidden rounded-full bg-paper">{p.thumb ? <img src={p.thumb} alt="" className="size-full object-contain" /> : null}</span>
          <span className="max-w-40 truncate">{p.name}</span>
          <button type="button" onClick={() => onToggle(p)} aria-label={`Remove ${p.name}`} className="inline-flex size-9 items-center justify-center rounded-full hover:bg-paper/70 focus-visible:focus-ring">
            <X className="size-4" aria-hidden />
          </button>
        </span>
      ))}
      <span className="ml-auto px-2 text-xs text-ink-3">{picks.length} of {max}</span>
    </div>
  )
}

interface Roster {
  /** uid -> display position; later arrivals (sheet puppets) are appended. */
  order: Map<string, number>
  /** Catalog assets / projects shown as ready puppets, so their catalog tiles are hidden. */
  assets: Set<string>
  projects: Set<string>
}

function makeRoster(items: readonly PuppetFeedItem[]): Roster {
  // Ready first; within that, as the server ordered them. Fixed from here on.
  const sorted = [...items].sort((a, b) => Number(b.story_ready) - Number(a.story_ready))
  const ready = sorted.filter((p) => p.story_ready)
  return {
    order: new Map(sorted.map((p, i) => [p.uid, i])),
    assets: new Set(ready.flatMap((p) => (p.asset_uid ? [p.asset_uid] : []))),
    projects: new Set(ready.map((p) => p.project_id)),
  }
}

export function PickTile({ pick, picked, disabled, note, onToggle }: { pick: CastPick; picked: boolean; disabled: boolean; note: string | null; onToggle(p: CastPick): void }) {
  return (
    <li>
      <button
        type="button"
        aria-pressed={picked}
        aria-label={`${pick.name}${note ? ` (${note.toLowerCase()})` : ''}`}
        disabled={disabled}
        onClick={() => onToggle(pick)}
        data-uid={pick.uid}
        className={cn(
          'group relative flex w-full flex-col overflow-hidden rounded-2xl border bg-paper text-left transition-[box-shadow,transform] focus-visible:focus-ring',
          'hover:-translate-y-0.5 hover:shadow-pop disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:translate-y-0 disabled:hover:shadow-none',
          picked ? 'border-accent ring-2 ring-accent' : 'border-line',
        )}
      >
        <PuppetThumb src={pick.thumb} name={pick.name} />
        {picked ? (
          <span className="absolute right-2 top-2 inline-flex size-7 items-center justify-center rounded-full bg-accent text-accent-ink shadow-card" aria-hidden>
            <Check className="size-4" />
          </span>
        ) : null}
        <span className="flex min-h-14 flex-col justify-center px-2.5 py-2">
          <span className="truncate text-sm font-semibold text-ink">{pick.name}</span>
          <span className="truncate text-xs text-ink-3">{note ?? ' '}</span>
        </span>
      </button>
    </li>
  )
}
